import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@marine/ui/badge';
import { Button } from '@marine/ui/button';
import { Input } from '@marine/ui/input';
import { Label } from '@marine/ui/label';
import { Separator } from '@marine/ui/separator';
import {
  usePlatformAiSettings,
  useUpdatePlatformAiSettings,
  type UpdatePlatformAiSettingsInput,
} from '@/modules/platform/services/aiSettingsApi';

type ProviderKey =
  | 'anthropic_api_key'
  | 'openrouter_api_key'
  | 'perplexity_api_key'
  | 'openai_api_key';

type ConfigKey = 'anthropic' | 'openrouter' | 'perplexity' | 'openai';

const PROVIDERS: Array<{
  field: ProviderKey;
  label: string;
  placeholder: string;
  description: string;
  configKey: ConfigKey;
}> = [
  {
    field:       'anthropic_api_key',
    label:       'Anthropic',
    placeholder: 'sk-ant-…',
    description: 'Used by ai-engine for Claude-powered spec lookup, description generation, and synthesis.',
    configKey:   'anthropic',
  },
  {
    field:       'openrouter_api_key',
    label:       'OpenRouter',
    placeholder: 'sk-or-…',
    description: 'Routes legacy Laravel AI calls (and ai-engine fallbacks) to OpenAI / Anthropic / Google models.',
    configKey:   'openrouter',
  },
  {
    field:       'perplexity_api_key',
    label:       'Perplexity',
    placeholder: 'pplx-…',
    description: 'Web-augmented search used during boat spec lookups and competitor research.',
    configKey:   'perplexity',
  },
  {
    field:       'openai_api_key',
    label:       'OpenAI',
    placeholder: 'sk-…',
    description: 'Direct OpenAI access for embeddings and direct GPT-4 calls outside OpenRouter.',
    configKey:   'openai',
  },
];

/**
 * Platform → AI Settings → Keys tab (Phase 39.2).
 *
 * Extracted from the pre-39.2 PlatformAiSettingsPage body — tab-agnostic
 * content lives here so the page shell can add sibling tabs without moving
 * the content around. Functionality unchanged from 39.1 (save/clear the
 * four provider keys on the singleton platform_ai_settings row).
 */
export function KeysTab() {
  const { data, isLoading } = usePlatformAiSettings();
  const updateMutation = useUpdatePlatformAiSettings();
  const [drafts, setDrafts] = useState<Partial<Record<ProviderKey, string>>>({});

  const handleSave = (field: ProviderKey, value: string) => {
    const payload: UpdatePlatformAiSettingsInput = { [field]: value || null };
    updateMutation.mutate(payload, {
      onSuccess: () => {
        toast.success(`${labelFor(field)} key saved`);
        setDrafts((prev) => ({ ...prev, [field]: '' }));
      },
      onError: () => {
        toast.error(`Failed to save ${labelFor(field)} key`);
      },
    });
  };

  const handleClear = (field: ProviderKey) => {
    if (!confirm(`Clear the platform-wide ${labelFor(field)} key? Tenants without their own override will get 503 errors until a new key is set.`)) {
      return;
    }
    updateMutation.mutate({ [field]: null } as UpdatePlatformAiSettingsInput, {
      onSuccess: () => {
        toast.success(`${labelFor(field)} key cleared`);
        setDrafts((prev) => ({ ...prev, [field]: '' }));
      },
      onError: () => {
        toast.error(`Failed to clear ${labelFor(field)} key`);
      },
    });
  };

  if (isLoading) {
    return (
      <div className="flex h-48 items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-center">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
          <div className="text-sm font-medium">Loading AI settings…</div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="grid gap-4 lg:grid-cols-2">
        {PROVIDERS.map((p) => {
          const masked = data?.masked_keys[p.configKey];
          const configured = data?.configured[p.configKey] ?? false;
          const draft = drafts[p.field] ?? '';

          return (
            <div key={p.field} className="space-y-3 rounded-lg border border-border p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">{p.label}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">{p.description}</p>
                </div>
                {configured ? (
                  <Badge variant="success" appearance="light" size="sm">
                    Configured
                  </Badge>
                ) : (
                  <Badge variant="secondary" size="sm">
                    Not configured
                  </Badge>
                )}
              </div>

              <div className="space-y-1">
                <Label htmlFor={p.field} className="text-xs text-muted-foreground">
                  {configured ? `Current: ${masked}` : 'No key set'}
                </Label>
                <Input
                  id={p.field}
                  type="password"
                  placeholder={p.placeholder}
                  value={draft}
                  onChange={(e) => setDrafts((prev) => ({ ...prev, [p.field]: e.target.value }))}
                  disabled={updateMutation.isPending}
                />
              </div>

              <div className="flex justify-end gap-2">
                {configured && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleClear(p.field)}
                    disabled={updateMutation.isPending}
                  >
                    Clear
                  </Button>
                )}
                <Button
                  type="button"
                  size="sm"
                  onClick={() => handleSave(p.field, draft)}
                  disabled={updateMutation.isPending || draft === ''}
                >
                  {updateMutation.isPending ? (
                    <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                  ) : null}
                  Save
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <Separator />

      <div className="text-xs text-muted-foreground">
        {data?.updated_at ? (
          <>Last updated {new Date(data.updated_at).toLocaleString()}{data.updated_by ? ` by ${data.updated_by.full_name}` : ''}</>
        ) : (
          'No keys configured yet.'
        )}
      </div>
    </>
  );
}

function labelFor(field: ProviderKey): string {
  return PROVIDERS.find((p) => p.field === field)?.label ?? field;
}
